/**
 * Lilia's tools, in one list (navigation handoff, Olivia 27 Sep 2026).
 *
 * The app-bar crumb names them, the Lilia menu pins them, Home gives each one
 * a card. A tool earns a place here when it has things of its own to list:
 * Symbols and Cross-references act on the open paper and stay editor panels,
 * and New from structure folds into Templates.
 */
import {
  IconArrowsExchange, IconBraces, IconFileText, IconLayoutGrid, IconMath,
  IconQuote, IconSettings, IconTable, type Icon,
} from "@tabler/icons-react";
import i18n from "i18next";
import { paths } from "./addresses";

export type ToolKey =
  | "documents" | "tables" | "references" | "equations"
  | "templates" | "snippets" | "import" | "settings";

export interface ToolDef {
  key: ToolKey;
  name: string;
  /** One line, for All tools and Home's More tools. */
  desc: string;
  path: string;
  icon: Icon;
  /** Home's corner action: what "new" means in this tool. */
  create?: { label: string; to: string };
  /** Addresses that belong to the tool, beyond `path` and what's under it. */
  also?: string[];
}

/** A tool whose words (name, line, corner action) read in the interface's language. */
function tool(def: Omit<ToolDef, "name" | "desc" | "create"> & { create?: { to: string } }): ToolDef {
  const { create, ...rest } = def;
  return {
    ...rest,
    get name() { return i18n.t(`shell:tools.${def.key}.name`); },
    get desc() { return i18n.t(`shell:tools.${def.key}.desc`); },
    ...(create ? { create: { to: create.to, get label() { return i18n.t(`shell:tools.${def.key}.create`); } } } : {}),
  };
}

export const TOOLS: ToolDef[] = [
  tool({ key: "documents", path: paths.documents, icon: IconFileText, create: { to: "lilia:new-document" }, also: ["/d/"] }),
  tool({ key: "tables", path: paths.tables, icon: IconTable, create: { to: paths.tables + "?new=1" } }),
  tool({ key: "references", path: paths.references, icon: IconQuote }),
  tool({ key: "equations", path: paths.equations, icon: IconMath }),
  tool({ key: "templates", path: paths.templates, icon: IconLayoutGrid, also: ["/new-from-structure"] }),
  tool({ key: "snippets", path: paths.snippets, icon: IconBraces }),
  tool({ key: "import", path: paths.import, icon: IconArrowsExchange, create: { to: paths.import + "?tab=import" }, also: ["/import-review", "/import-summary", "/jobs", "/convert"] }),
  tool({ key: "settings", path: "/settings", icon: IconSettings }),
];

export const toolByKey = (key: string): ToolDef | undefined => TOOLS.find((t) => t.key === key);

const under = (pathname: string, root: string) =>
  root.endsWith("/") ? pathname.startsWith(root) : pathname === root || pathname.startsWith(root + "/");

/** The tool an address belongs to, if any. `/d/:id` is Documents'. */
export function toolForPath(pathname: string): ToolDef | undefined {
  return TOOLS.find((t) => under(pathname, t.path) || (t.also ?? []).some((a) => under(pathname, a)));
}

/** The default pinned order: every tool, as drawn in 1b. */
export const DEFAULT_PINNED: ToolKey[] = TOOLS.map((t) => t.key);
